import React, { useState, useMemo } from 'react';
import {
  Box,
  Flex,
  Text,
  Heading,
  Icon,
  Spinner,
  Button,
  FormControl,
  FormLabel,
  VStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { Cake, MagnifyingGlass, Info, Eye } from 'phosphor-react';
import { useDatasEmpresa, IAniversarioEmpresa } from '../../../hooks/useDatasEmpresa';
import { IDataNoPagination } from '../../../types/shared';
import { formatCNPJ } from '../../../utils/FormatFunctions';

const ITEMS_TO_SHOW = 5;

const months = [
  { value: 1, label: 'Janeiro' },
  { value: 2, label: 'Fevereiro' },
  { value: 3, label: 'Março' },
  { value: 4, label: 'Abril' },
  { value: 5, label: 'Maio' },
  { value: 6, label: 'Junho' },
  { value: 7, label: 'Julho' },
  { value: 8, label: 'Agosto' },
  { value: 9, label: 'Setembro' },
  { value: 10, label: 'Outubro' },
  { value: 11, label: 'Novembro' },
  { value: 12, label: 'Dezembro' },
];

export function AniversariosEmpresasCard() {
  const { getAniversarios } = useDatasEmpresa();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const currentMonth = new Date().getMonth() + 1;

  const [mesInicial, setMesInicial] = useState(currentMonth);
  const [mesFinal, setMesFinal] = useState(currentMonth);
  const [appliedMesInicial, setAppliedMesInicial] = useState(currentMonth);
  const [appliedMesFinal, setAppliedMesFinal] = useState(currentMonth);

  const appliedFilters = useMemo(
    () => ({
      mes_inicial: appliedMesInicial,
      mes_final: appliedMesFinal,
    }),
    [appliedMesInicial, appliedMesFinal],
  );

  const queryKey = ['aniversariosEmpresas', appliedFilters];

  const { data, isFetching, isError } = useQuery<IDataNoPagination<IAniversarioEmpresa[]>>({
    queryKey,
    queryFn: getAniversarios,
    enabled: !!appliedMesInicial && !!appliedMesFinal,
  });

  const handleApplyFilters = () => {
    setAppliedMesInicial(mesInicial);
    setAppliedMesFinal(mesFinal);
  };

  const aniversariosData = data?.data;
  const totalItems = aniversariosData?.length || 0;
  const hasMore = totalItems > ITEMS_TO_SHOW;
  const displayedItems = aniversariosData?.slice(0, ITEMS_TO_SHOW) || [];

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const calculateYearsAndMonths = (dateStr: string | null) => {
    if (!dateStr) return null;

    const startDate = new Date(dateStr);
    const now = new Date();

    let years = now.getFullYear() - startDate.getFullYear();
    let diffMonths = now.getMonth() - startDate.getMonth();

    if (now.getDate() < startDate.getDate()) {
      diffMonths -= 1;
    }

    if (diffMonths < 0) {
      years -= 1;
      diffMonths += 12;
    }

    return { years, diffMonths };
  };

  const formatYearsAndMonths = (years: number, totalMonths: number) => {
    if (years < 1) {
      return `${totalMonths} ${totalMonths === 1 ? 'mês' : 'meses'}`;
    }
    const yearText = `${years} ${years === 1 ? 'ano' : 'anos'}`;
    if (totalMonths === 0) {
      return yearText;
    }
    const monthText = `${totalMonths} ${totalMonths === 1 ? 'mês' : 'meses'}`;
    return `${yearText} e ${monthText}`;
  };

  const getYearsColorScheme = (years: number) => {
    if (years >= 5) return 'green';
    if (years >= 2) return 'blue';
    return 'gray';
  };

  const getMonthLabel = (monthValue: number) => {
    const month = months.find(m => m.value === monthValue);
    return month?.label || String(monthValue);
  };

  const getFilterLabel = () => {
    if (appliedMesInicial === appliedMesFinal) {
      return getMonthLabel(appliedMesInicial);
    }
    return `${getMonthLabel(appliedMesInicial)} a ${getMonthLabel(appliedMesFinal)}`;
  };

  const renderTable = (items: IAniversarioEmpresa[], isModal = false) => (
    <Table size="sm">
      <Thead position="sticky" top={0} bg="white" zIndex={1}>
        <Tr>
          <Th>ID</Th>
          <Th>Razão Social</Th>
          <Th>CNPJ</Th>
          <Th>Cliente Desde</Th>
          <Th isNumeric>Anos/Meses</Th>
        </Tr>
      </Thead>
      <Tbody>
        {items.map(aniversario => {
          const timeData = calculateYearsAndMonths(aniversario.dt_cli_desde);
          return (
            <Tr key={aniversario.external_id} _hover={{ bg: 'gray.50' }}>
              <Td>
                <Badge colorScheme="purple">{aniversario.external_id}</Badge>
              </Td>
              <Td>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  noOfLines={isModal ? undefined : 1}
                  maxW={isModal ? 'none' : '200px'}
                >
                  {aniversario.razao_social}
                </Text>
              </Td>
              <Td>
                <Text fontSize="sm" color="gray.600">
                  {formatCNPJ(aniversario.cnpj)}
                </Text>
              </Td>
              <Td>{formatDate(aniversario.dt_cli_desde)}</Td>
              <Td isNumeric>
                {timeData ? (
                  <Badge colorScheme={getYearsColorScheme(timeData.years)}>
                    {formatYearsAndMonths(timeData.years, timeData.diffMonths)}
                  </Badge>
                ) : (
                  <Text fontSize="sm" color="gray.400">
                    Sem dados
                  </Text>
                )}
              </Td>
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );

  return (
    <>
      <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="xl" boxShadow="lg" h="100%">
        <Flex justify="space-between" align="center" mb={4}>
          <Heading size={{ base: 'sm', md: 'md' }} color="gray.700">
            <Flex align="center" gap={2}>
              <Icon as={Cake} boxSize={{ base: 4, md: 5 }} />
              Aniversários de Tempo de Casa
              <Tooltip
                label="Lista de empresas que fazem aniversário de cliente no intervalo de meses selecionado, independente do ano. Filtra pelo mês da data em que a empresa se tornou cliente."
                placement="top"
                hasArrow
                bg="gray.700"
                color="white"
                fontSize="xs"
                px={3}
                py={2}
                borderRadius="md"
              >
                <Box as="span" cursor="help">
                  <Icon as={Info} boxSize={4} color="gray.400" />
                </Box>
              </Tooltip>
            </Flex>
          </Heading>
        </Flex>

        <VStack spacing={4} align="stretch">
          <Flex gap={3} flexDirection={{ base: 'column', md: 'row' }} align="flex-end">
            <FormControl size="sm">
              <FormLabel fontSize="sm" mb={1}>
                Mês Inicial
              </FormLabel>
              <Select
                size="sm"
                value={mesInicial}
                onChange={e => setMesInicial(Number(e.target.value))}
                bg="white"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl size="sm">
              <FormLabel fontSize="sm" mb={1}>
                Mês Final
              </FormLabel>
              <Select
                size="sm"
                value={mesFinal}
                onChange={e => setMesFinal(Number(e.target.value))}
                bg="white"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <Button
              size="sm"
              colorScheme="blue"
              leftIcon={<MagnifyingGlass size={16} />}
              onClick={handleApplyFilters}
              minW="100px"
            >
              Buscar
            </Button>
          </Flex>

          <Box position="relative">
            <Box overflowX="auto" maxH="180px" overflow="hidden">
              {isFetching && (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color="blue.500" />
                </Flex>
              )}
              {!isFetching && isError && (
                <Flex justify="center" py={8}>
                  <Text color="red.500">Erro ao carregar dados</Text>
                </Flex>
              )}
              {!isFetching && !isError && displayedItems.length > 0 && renderTable(displayedItems)}
              {!isFetching && !isError && (!aniversariosData || aniversariosData.length === 0) && (
                <Flex justify="center" py={8}>
                  <Text color="gray.500">Nenhum aniversário encontrado no período</Text>
                </Flex>
              )}
            </Box>

            {/* Gradiente fade */}
            {hasMore && (
              <Box
                position="absolute"
                bottom={0}
                left={0}
                right={0}
                h="60px"
                bgGradient="linear(to-t, white 20%, transparent)"
                pointerEvents="none"
              />
            )}
          </Box>

          {totalItems > 0 && (
            <Flex justify="center" align="center" mt={2}>
              {hasMore ? (
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="blue"
                  leftIcon={<Eye size={16} />}
                  onClick={onOpen}
                >
                  Ver todos ({totalItems})
                </Button>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  Total: {totalItems} registro(s)
                </Text>
              )}
            </Flex>
          )}
        </VStack>
      </Box>

      {/* Modal com todos os registros */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Flex align="center" gap={2}>
              <Icon as={Cake} boxSize={5} color="gray.600" />
              Aniversário de Tempo de Casa - {getFilterLabel()}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box overflowX="auto">
              {aniversariosData &&
                aniversariosData.length > 0 &&
                renderTable(aniversariosData, true)}
            </Box>
            <Flex justify="flex-end" mt={4}>
              <Text fontSize="sm" color="gray.500">
                Total: {totalItems} registro(s)
              </Text>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
